import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import type { Editor } from '@tiptap/react';
import { getMetaCharacter } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { BoldIcon, ItalicIcon, UnderlineIcon } from '@tyro/icons';
import type { BaseMenuTools } from '../../@types/base-menu-tools';
import ToolbarColorPicker from '../colours/color-picker';
import { ToggleButton } from '../toolbar-buttons/toggle-button';

export interface FontStylingProps {
  editor: Editor | null;
  isToolVisible: (tool: BaseMenuTools) => boolean;
}

export enum PickerModeOptions {
  COLOUR = 'colour',
  HIGHLIGHT = 'highlight',
}

export default function FontStyling({
  editor,
  isToolVisible,
}: FontStylingProps) {
  const { t } = useTranslation(['common']);
  const metaCharacter = getMetaCharacter();

  if (!editor) {
    return null;
  }

  return (
    <ToggleButtonGroup
      aria-label="text formatting"
      size="small"
      exclusive
      sx={{
        '.MuiToggleButtonGroup-grouped:not(:first-of-type)': {
          borderLeft: 'none',
        },
        border: 'none',
        alignItems: 'center',
        backgroundColor: 'slate.50',
      }}
    >
      {isToolVisible('bold') && (
        <ToggleButton
          title={t('common:tooltipTitles.bold', {
            shortcut: `${metaCharacter} + B`,
          })}
          selected={editor.isActive('bold')}
          onClick={() => editor?.chain().toggleBold().run()}
          value="bold"
        >
          <BoldIcon />
        </ToggleButton>
      )}
      {isToolVisible('italic') && (
        <ToggleButton
          title={t('common:tooltipTitles.italic', {
            shortcut: `${metaCharacter} + I`,
          })}
          onClick={() => editor?.chain().toggleItalic().run()}
          selected={editor?.isActive('italic')}
          value="italic"
        >
          <ItalicIcon />
        </ToggleButton>
      )}
      {isToolVisible('underline') && (
        <ToggleButton
          title={t('common:tooltipTitles.underline', {
            shortcut: `${metaCharacter} + U`,
          })}
          onClick={() => editor.chain().toggleUnderline().run()}
          selected={editor?.isActive('underline')}
          value="underline"
        >
          <UnderlineIcon />
        </ToggleButton>
      )}
      {isToolVisible('textColor') && (
        <ToolbarColorPicker
          editor={editor}
          pickerMode={PickerModeOptions.COLOUR}
        />
      )}
      {isToolVisible('highlight') && (
        <ToolbarColorPicker
          editor={editor}
          pickerMode={PickerModeOptions.HIGHLIGHT}
        />
      )}
    </ToggleButtonGroup>
  );
}
